import React from 'react';
import { ITableDataSource } from 'models/table';
import { colors } from 'theme/colors';
import Grid from '@mui/material/Grid';
import { Typography } from '@mui/material';
import { Chips, EChipsVariants } from 'components/atoms/Chips';
import Button from 'components/atoms/Button';
import { EButtonVariants } from 'constants/Buttons';
import { EIconTypes } from 'constants/Icons';
import { useGlobalProperty } from 'models/globalPropertyContext';
import { ERouteLinks } from 'models/route';
import { Link } from 'react-router-dom';
import { emptyFieldHolder } from 'components/molecules/CardList';
import { formatDate } from 'utils/date';
import { getLicenceStatusChipVariant } from '../LegalEntityViewLicencePage/helpers';
import { ATTACHMENT_TYPE_CASE, ATTACHMENT_TYPE_LICENCE } from '../helpers';
import { renderStatusCaseFieldColor } from 'pages/CaseManagementPage/helpers';

interface ILegalEntityAttachmentTableCard {
  data: ITableDataSource;
  handleView: (
    legalUniqueId: string,
    attachmentUniqueId: string,
    licenceUniqueId?: string,
    caseUniqueId?: string,
  ) => void;
  handleDownload: (
    legalUniqueId: string,
    attachmentUniqueId: string,
    fileName: string,
    licenceUniqueId?: string,
    caseUniqueId?: string,
  ) => void;
  handleDelete: (attachmentUniqueId: string) => void;
  isLicenceAuthorityValid: boolean;
  hasPermissionsAttachments: boolean;
  hasPermissionsLicence: boolean;
  type?: string | null;
  t: (key: string) => string;
  showLicenceColumns: boolean;
}

const LegalEntityAttachmentTableCard = ({
  data,
  handleView,
  handleDownload,
  handleDelete,
  isLicenceAuthorityValid,
  hasPermissionsAttachments,
  hasPermissionsLicence,
  type,
  t,
  showLicenceColumns,
}: ILegalEntityAttachmentTableCard) => {
  const { shortDateFormat } = useGlobalProperty();

  const renderDate = (date: string) => formatDate(date, shortDateFormat);

  return (
    <Grid
      sx={{
        backgroundColor: colors.gray100,
        border: `1px solid ${colors.gray200}`,
        borderRadius: '4px',
        overflow: 'hidden',
        padding: '20px',
        rowGap: 3,
      }}
    >
      <Grid size={{ xs: 12, lg: 3 }}>
        <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
          {t('legalEntityDetails.attachments.fields.createdDate')}
        </Typography>
        <Typography variant="body2">{renderDate(data.createdDate)}</Typography>
      </Grid>
      <Grid size={{ xs: 12, lg: 3 }}>
        <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
          {t('legalEntityDetails.attachments.fields.addedBy')}
        </Typography>
        <Typography variant="body2">{data.addedBy}</Typography>
      </Grid>
      <Grid size={{ xs: 12, lg: 3 }}>
        <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
          {t('legalEntityDetails.attachments.fields.attachmentType')}
        </Typography>
        <Typography variant="body2">{data.documentTypeUniqueId}</Typography>
      </Grid>
      <Grid size={{ xs: 12, lg: 3 }}>
        <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
          {t('legalEntityDetails.attachments.fields.filename')}
        </Typography>
        <Typography variant="body2">{data.fileName}</Typography>
      </Grid>

      {showLicenceColumns && (
        <>
          <Grid size={{ xs: 12, lg: 3 }}>
            <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
              {t('legalEntityDetails.attachments.fields.fileType')}
            </Typography>
            <Typography variant="body2">{data.fileType}</Typography>
          </Grid>
          <Grid size={{ xs: 12, lg: 3 }}>
            <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
              {t('legalEntityDetails.attachments.fields.number')}
            </Typography>
            {data.licenceUniqueId && type && hasPermissionsLicence && (
              <Link
                to={`${ERouteLinks.LegalEntityViewLicence.replace(
                  ':id',
                  data.legalUniqueId,
                ).replace(':licenceId', data.licenceUniqueId)}?type=${type}&from=le`}
                style={{
                  color: colors.primary500,
                  fontWeight: 600,
                  textDecoration: 'none',
                }}
              >
                {data.parentObjectIdNumber}
              </Link>
            )}
            {data.caseUniqueId && hasPermissionsLicence && (
              <Link
                to={`${ERouteLinks.LegalEntityCasesDetailsPage.replace(
                  ':id',
                  data.legalUniqueId,
                ).replace(':caseId', data.caseUniqueId)}?type=${type}&from=le`}
                style={{
                  color: colors.primary500,
                  fontWeight: 600,
                  textDecoration: 'none',
                }}
              >
                {data.parentObjectIdNumber}
              </Link>
            )}

            {((!data.caseUniqueId && !data.licenceUniqueId) || !hasPermissionsLicence) && (
              <Typography variant="body2" sx={{ color: colors.primary500, fontWeight: 600 }}>
                {emptyFieldHolder(data.parentObjectIdNumber)}
              </Typography>
            )}
          </Grid>
          <Grid size={{ xs: 12, lg: 3 }}>
            <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
              {t('legalEntityDetails.attachments.fields.type')}
            </Typography>
            {data.licenceUniqueId && (
              <Chips text={ATTACHMENT_TYPE_LICENCE} variant={EChipsVariants.primary} />
            )}
            {data.caseUniqueId && (
              <Chips text={ATTACHMENT_TYPE_CASE} variant={EChipsVariants.primary} fullWidth />
            )}
          </Grid>
          <Grid size={{ xs: 12, lg: 3 }}>
            <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
              {t('legalEntityDetails.attachments.fields.licenceCode')}
            </Typography>
            <Typography variant="body2">{data.licenceCode || '-'}</Typography>
          </Grid>
          <Grid size={{ xs: 12, lg: 3 }}>
            <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
              {t('legalEntityDetails.attachments.fields.status')}
            </Typography>
            {data.licenceUniqueId &&
              data.parentObjectStatusUniqueId &&
              data.objectStatus !== '-' && (
                <Chips
                  text={data.objectStatus}
                  variant={getLicenceStatusChipVariant(data.parentObjectStatusUniqueId)}
                  fullWidth
                />
              )}
            {data.caseUniqueId &&
              data.parentObjectStatusUniqueId &&
              data.objectStatus !== '-' && (
                <Chips
                  text={data.objectStatus}
                  variant={renderStatusCaseFieldColor({
                    statusUniqueId: data.parentObjectStatusUniqueId,
                  })}
                  fullWidth
                />
              )}
          </Grid>
        </>
      )}

      <Grid
        container
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          alignSelf: 'center',
          borderTop: {
            xs: `1px solid ${colors.primary100}`,
            lg: 'none',
          },
          mt: {
            xs: 0,
          },
        }}
        spacing={1}
      >
        <Grid size={{ xs: 12, sm: data.licenceUniqueId ? 6 : 4 }}>
          <Button
            id={`view-attachment-${data.uniqueId}`}
            variant={EButtonVariants.outlined}
            icon={EIconTypes.view}
            onClick={() =>
              handleView(
                data.legalUniqueId as string,
                data.uniqueId as string,
                data.licenceUniqueId as string,
                data.caseUniqueId as string,
              )
            }
            label={t('legalEntityDetails.attachments.view.button')}
            fullWidth
          />
        </Grid>
        <Grid size={{ xs: 12, sm: data.licenceUniqueId ? 6 : 4 }}>
          <Button
            id={`download-attachment-${data.uniqueId}`}
            variant={EButtonVariants.outlined}
            icon={EIconTypes.download}
            onClick={() =>
              handleDownload(
                data.legalUniqueId as string,
                data.uniqueId as string,
                data.fileName as string,
                data.licenceUniqueId as string,
                data.caseUniqueId as string,
              )
            }
            label={t('legalEntityDetails.attachments.download.button')}
            fullWidth
          />
        </Grid>
        {!data.licenceUniqueId && hasPermissionsAttachments && isLicenceAuthorityValid && (
          <Grid size={{ xs: 12, sm: 4 }}>
            <Button
              id={`delete-attachment-${data.uniqueId}`}
              variant={EButtonVariants.outlined}
              icon={EIconTypes.delete}
              onClick={() => handleDelete(data.uniqueId as string)}
              label={t('legalEntityDetails.attachments.delete.button')}
              fullWidth
            />
          </Grid>
        )}
      </Grid>
    </Grid>
  );
};

export { LegalEntityAttachmentTableCard };
